import { useState, type FormEvent } from 'react';
import { readTerminalId } from '../lib/terminal';
import { useAuth } from '../store/auth';
import { BrandMark, Icon } from './icons';
import { LANGUAGES, useLanguage, useT, type MessageKey } from '../i18n';

/** The registers a fresh install ships with. */
const TERMINALS = [
  { id: 'terminal:1', n: 1 },
  { id: 'terminal:2', n: 2 },
];

/**
 * Sign-in.
 *
 * The demo accounts are listed on screen because this build ships with a seeded store and
 * the first thing anyone does is log in. `production` hides them - the server refuses those
 * credentials outright, so the buttons are a development convenience, not a backdoor.
 */
export function LoginScreen() {
  const { signIn, signingIn, error } = useAuth();
  const t = useT();
  const language = useLanguage((state) => state.language);
  const setLanguage = useLanguage((state) => state.setLanguage);
  const [username, setUsername] = useState('cashier@demo.local');
  const [password, setPassword] = useState('Demo@12345');
  // The register this browser last signed in on. Defaulting to Terminal 1 would silently move
  // the till to another drawer's shift after a reload.
  const [terminalId, setTerminalId] = useState(readTerminalId);
  const options = TERMINALS.some((terminal) => terminal.id === terminalId)
    ? TERMINALS
    : [...TERMINALS, { id: terminalId, n: null }];

  async function submit(event: FormEvent) {
    event.preventDefault();
    await signIn(username.trim(), password, terminalId);
  }

  function useAccount(nextUsername: string) {
    setUsername(nextUsername);
    setPassword('Demo@12345');
  }

  return (
    <div
      className="flex min-h-full items-center justify-center bg-ink p-4 sm:p-6"
      style={{
        backgroundImage:
          'radial-gradient(60rem 28rem at 50% -8rem, rgb(26 140 240 / 0.22), transparent 70%), radial-gradient(40rem 24rem at 100% 100%, rgb(10 95 174 / 0.18), transparent 70%)',
      }}
    >
      <main className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <BrandMark size={52} />
          <h1 className="mt-4 text-2xl font-bold tracking-tight text-on-ink">{t('login.store')}</h1>
          <p className="mt-1 text-sm text-on-ink-3">{t('login.subtitle')}</p>
          <div className="mt-3 flex gap-1.5" role="group" aria-label={t('shell.language')} data-testid="login-language">
            {LANGUAGES.map((entry) => (
              <button
                key={entry.code}
                type="button"
                lang={entry.code}
                aria-pressed={language === entry.code}
                className={'min-h-9 rounded-full border px-3 text-sm font-semibold ' + (language === entry.code ? 'border-on-ink/40 bg-on-ink/15 text-on-ink' : 'border-on-ink/15 text-on-ink-3 hover:text-on-ink')}
                style={'font' in entry ? { fontFamily: entry.font } : undefined}
                onClick={() => setLanguage(entry.code)}
              >
                {entry.label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className="panel space-y-4 p-5 sm:p-6" style={{ boxShadow: 'var(--ui-shadow-3)' }}>
          <h2 className="text-base font-semibold text-slate-900">{t('login.heading')}</h2>

          <div>
            <label htmlFor="username" className="field-label">
              {t('login.username')}
            </label>
            <input
              id="username"
              data-testid="login-username"
              className="field"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="username"
              autoFocus
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="field-label">
              {t('login.password')}
            </label>
            <input
              id="password"
              type="password"
              data-testid="login-password"
              className="field"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
            />
          </div>

          <div>
            <label htmlFor="terminal" className="field-label">
              {t('login.terminal')}
            </label>
            <select
              id="terminal"
              data-testid="login-terminal"
              className="field"
              value={terminalId}
              onChange={(event) => setTerminalId(event.target.value)}
            >
              {options.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.n === null ? option.id : t('login.terminalN', { n: option.n })}
                </option>
              ))}
            </select>
          </div>

          {error ? (
            <p role="alert" className="banner banner-danger items-start">
              <Icon name="alert" size={18} className="mt-px shrink-0" />
              <span>{error}</span>
            </p>
          ) : null}

          <button type="submit" className="btn btn-primary btn-lg w-full" data-testid="login-submit" disabled={signingIn}>
            {signingIn ? t('login.signingIn') : t('login.submit')}
          </button>
        </form>

        {import.meta.env.DEV ? (
          <div className="panel mt-4 p-4">
            <p className="mb-2 text-xs font-semibold tracking-wide text-slate-500 uppercase">
              {t('login.demo')}
            </p>
            <div className="grid grid-cols-3 gap-2">
              {(
                [
                  ['role.admin', 'admin@demo.local', 'login.demoAdmin'],
                  ['role.manager', 'manager@demo.local', 'login.demoManager'],
                  ['role.cashier', 'cashier@demo.local', 'login.demoCashier'],
                ] as const
              ).map(([label, address, hint]) => (
                <button
                  key={address}
                  type="button"
                  className="btn flex-col gap-0 px-2 py-2 text-sm leading-tight"
                  aria-pressed={username === address}
                  onClick={() => useAccount(address)}
                >
                  {t(label as MessageKey)}
                  <span className="text-[11px] font-normal text-slate-500">{t(hint as MessageKey)}</span>
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
}
